import React from "react";

import { DownOutlined, SearchOutlined } from "@ant-design/icons";

import StyledScrollbar from "./StyledScrollbar";

export const SELECT_DROPDOWN_ROW_HEIGHT = 32;
export const SELECT_DROPDOWN_MAX_HEIGHT = 256;

/**
 * Keep the down arrow when showSearch + prefix search icon.
 * AntD otherwise swaps suffix to SearchOutlined while open (duplicate search icons).
 * pointerEvents: none so clicks still open the dropdown.
 */
export const selectSearchSuffixIcon = (
  <DownOutlined style={{ pointerEvents: "none" }} aria-hidden />
);

/**
 * Searchable Select: prefix search icon + ↓ suffix (no trailing search).
 * Do not set menuItemSelectedIcon here — AntD shows a check only for mode="multiple"
 * (colored #C84A17 via tokens.css). Single-select = selected text/bg only, no tick.
 */
export function getSearchableSelectProps({ optionFilterProp = "label" } = {}) {
  return {
    showSearch: true,
    optionFilterProp,
    prefix: <SearchOutlined aria-hidden />,
    suffixIcon: selectSearchSuffixIcon,
  };
}

/** Count leaf options (supports AntD grouped `{ label, options: [] }`). */
export function countSelectOptions(options = []) {
  if (!Array.isArray(options)) {
    return 0;
  }
  return options.reduce((sum, opt) => {
    if (Array.isArray(opt?.options)) {
      return sum + opt.options.length;
    }
    return sum + 1;
  }, 0);
}

export function getSelectPopupHeight(optionCount, maxHeight = SELECT_DROPDOWN_MAX_HEIGHT) {
  return Math.min(
    Math.max(optionCount || 1, 1) * SELECT_DROPDOWN_ROW_HEIGHT,
    maxHeight,
  );
}

/**
 * popupRender + StyledScrollbar for AntD Select.
 * Short lists (fit in maxHeight) skip StyledScrollbar so a phantom scrollbar
 * does not appear for 2–3 options (Address / Role / etc.).
 */
export function renderSelectPopupScrollbar(menu, {
  optionCount = 0,
  maxHeight = SELECT_DROPDOWN_MAX_HEIGHT,
  className = "",
} = {}) {
  const estimatedHeight = Math.max(optionCount || 1, 1) * SELECT_DROPDOWN_ROW_HEIGHT;
  if (estimatedHeight <= maxHeight) {
    return menu;
  }
  return (
    <StyledScrollbar
      className={["SelectPopupScrollbar", className].filter(Boolean).join(" ")}
      style={{ height: maxHeight }}
      noScrollX
    >
      {menu}
    </StyledScrollbar>
  );
}

/** AntD 5: replace deprecated popupClassName with classNames.popup.root */
export function selectPopupClassNames(popupRootClassName, extraRoot = "SelectPopup--styledScroll") {
  const root = [extraRoot, popupRootClassName].filter(Boolean).join(" ");
  return root ? { popup: { root } } : undefined;
}
